import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon from "./Icon";
import {
  getEventSlots,
  getVehicleMakes,
  getVehicleModels,
  getVehicleYears,
  patchGuestForm,
  setSelectedSlot,
  setVehicleOptions,
  submitEventRegistration,
} from "../../store/events";
import { Button, GuestFormFields } from "./FormControls";
import {
  guestFields,
  formatEventDate,
  formatWindow,
  shopTitle,
} from "./nsdFields";
import "./events.css";

/* ── Classic booking screen ───────────────────────────────────────────────── */
const ClassicBooking = () => {
  const dispatch = useDispatch();
  const {
    event,
    slots,
    slotsStatus,
    selectedSlot,
    guestForm: v,
    vehicleOptions,
    submitting,
    slotConflict: conflict,
    submitError,
  } = useSelector((state) => state.events);

  const set = (p) => dispatch(patchGuestForm(p));

  // True once a submit has been running long enough to warrant a progress note.
  const [bookingSlow, setBookingSlow] = useState(false);
  useEffect(() => {
    if (!submitting) {
      setBookingSlow(false);
      return undefined;
    }
    const t = setTimeout(() => setBookingSlow(true), 8000);
    return () => clearTimeout(t);
  }, [submitting]);

  // The banners sit above the form, so bring them into view when one appears.
  const alertRef = useRef(null);
  useEffect(() => {
    if (conflict || submitError) {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [conflict, submitError]);

  useEffect(() => {
    dispatch(getVehicleYears());
  }, [dispatch]);

  useEffect(() => {
    if (v.year) dispatch(getVehicleMakes(v.year));
    else dispatch(setVehicleOptions({ makes: [], models: [] }));
  }, [v.year, dispatch]);

  useEffect(() => {
    if (v.year && v.make) dispatch(getVehicleModels(v.year, v.make));
    else dispatch(setVehicleOptions({ models: [] }));
  }, [v.year, v.make, dispatch]);

  // Fields the guest has left, so a required-but-empty field only complains once
  // they've moved on from it.
  const [touched, setTouched] = useState({});
  const onTouch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const fields = guestFields(event, v, vehicleOptions);
  const req = fields.filter((f) => f.required);

  const err = {};
  fields.forEach((f) => {
    const value = v[f.key];
    if (value && f.validate && !f.validate(value)) {
      err[f.key] = f.msg || "Please check this field.";
    } else if (touched[f.key] && f.required && !value) {
      err[f.key] = f.msg || "This field is required.";
    }
  });

  const formValid =
    Boolean(selectedSlot) &&
    req.every((f) => v[f.key] && (!f.validate || f.validate(v[f.key])));

  const submit = () => {
    if (!formValid) return;

    // No slot holds — the server rechecks capacity on submit and a conflict
    // refetches the grid, so nothing is patched locally here.
    dispatch(submitEventRegistration());
  };

  return (
    <div className="ev-wrap">
      <h1 className="ev-h1">{event.name}</h1>
      <div className="ev-meta">
        <span className="ev-meta-item">
          <Icon name="calendar" color="var(--brand-legacy-green)" />{" "}
          {formatEventDate(event.eventDate)}
        </span>
        <span className="ev-meta-item">
          <Icon name="clock" color="var(--brand-legacy-green)" />{" "}
          {formatWindow(event.startTime, event.endTime)}
        </span>
        <span className="ev-meta-item">
          <Icon name="pin" color="var(--brand-legacy-green)" />{" "}
          {shopTitle(event.shop?.name)}
        </span>
      </div>
      <p className="ev-lead">
        {event.description ||
          "We're serving our neighbors with free oil changes and basic repairs. Pick a time below and tell us about your vehicle."}
      </p>

      <h2 className="ev-head">Pick a time</h2>
      <p className="ev-sub">Choose the time that's most convenient for you.</p>
      {conflict && (
        <div className="ev-conflict" ref={alertRef}>
          Sorry, that time was just taken. Please choose another slot below.
        </div>
      )}
      {submitError === "timeout" && (
        <div className="ev-conflict" ref={alertRef}>
          We didn't hear back in time, so we can't confirm your spot yet. You
          may already be on the list, so please call{" "}
          {shopTitle(event.shop?.name) || "the shop"}
          {event.shop?.phone ? ` at ${event.shop.phone}` : ""} to check before
          booking again.
        </div>
      )}
      {submitError === "failed" && (
        <div className="ev-conflict" ref={alertRef}>
          Something went wrong while booking your spot. Please try again, or
          call {shopTitle(event.shop?.name) || "the shop"}
          {event.shop?.phone ? ` at ${event.shop.phone}` : ""} if it keeps
          happening.
        </div>
      )}
      {Object.keys(touched).length > 0 && !selectedSlot && !conflict && (
        <div className="ev-required-msg">Please choose a time.</div>
      )}
      {slotsStatus === "loading" && (
        <p className="ev-sub">Loading available times…</p>
      )}
      {slotsStatus === "error" && (
        <div className="ev-conflict">
          We couldn't load available times.{" "}
          <button
            type="button"
            className="ev-inline-link"
            onClick={() => dispatch(getEventSlots(event.event_id))}
          >
            Try again
          </button>
        </div>
      )}
      {slotsStatus === "ready" && slots.length === 0 && (
        <p className="ev-sub">There are no times available for this event.</p>
      )}
      <div className="ev-slots">
        {slots.map((s) => {
          const full = s.available <= 0;
          return (
            <button
              key={s.time}
              disabled={full}
              className={`ev-slot${
                selectedSlot === s.time ? " is-selected" : ""
              }`}
              onClick={() => dispatch(setSelectedSlot(s.time))}
            >
              {s.time}
            </button>
          );
        })}
      </div>

      <h2 className="ev-head">Tell us about you &amp; your vehicle</h2>
      <GuestFormFields
        event={event}
        v={v}
        set={set}
        err={err}
        onTouch={onTouch}
        vehicleOptions={vehicleOptions}
      />

      <div className="ev-actions">
        <Button
          kind="primary"
          icon="check"
          onClick={submit}
          disabled={submitting || !formValid}
          busy={submitting}
        >
          {submitting ? "Booking your spot…" : "Book my spot"}
        </Button>
        <span className="ev-note">
          {submitting && bookingSlow
            ? "Still working. Please don't close this page or book again."
            : "You'll get a confirmation email you can use to cancel."}
        </span>
      </div>
    </div>
  );
};

export default ClassicBooking;
